import { useTranslation } from 'react-i18next';
import type { TimelineShot } from '@/types/timeline';
import animaticIcon from '@/assets/shotBadges/Animatics.png?inline';
import videoIcon from '@/assets/shotBadges/playbutton.png?inline';
import staticImageIcon from '@/assets/shotBadges/ImageSimple.png?inline';
import styles from './ShotTypeBadge.module.css';

export type ShotTypeIndicatorKind = 'animatic' | 'video' | 'staticImage';

export interface ShotTypeIndicatorOptions {
  /** OR'd with `shot.videoObjectUrl != null`. Exists for ShotGrid (AI workspace), which derives
   *  video state from `projectStore.videoShots` membership rather than the shot field directly.
   *  The two can diverge in-session: sqliteBridge's live-refresh merge (a brand-new shot pushed
   *  by Unity mid-session) grafts a pendingTileUpdate's `videoObjectUrl` straight onto the shot,
   *  but `incoming.videoShots` is only ever a FILTER of the prior `videoShots` array — it can
   *  drop stale rows, never add a shot id that array never held — so the new shot's video can be
   *  set on the shot without a matching `videoShots` entry for at least one refresh cycle. The OR
   *  covers both directions regardless of which side lags. */
  hasVideo?: boolean;
}

/** Web port of the Unity canvas shot-type indicator priority chain
 *  (ShotListItemController.cs UpdateUI: Animatic > AI-video > Static image > none).
 *  isAnimatic == HasAnimaticsSequence (shotcategorytype 'Initial');
 *  videoObjectUrl != null (or opts.hasVideo) == HasVideoGeneration (applied history entry of
 *  assetType 'video'); hasStaticImage == HasStaticImage && IsStaticImageEnabled (ANDed by
 *  previsLoader). */
export function shotTypeIndicator(shot: TimelineShot, opts?: ShotTypeIndicatorOptions): ShotTypeIndicatorKind | null {
  if (shot.isAnimatic) return 'animatic';
  if (shot.videoObjectUrl != null || opts?.hasVideo === true) return 'video';
  if (shot.hasStaticImage) return 'staticImage';
  return null;
}

const ICONS: Record<ShotTypeIndicatorKind, string> = {
  animatic: animaticIcon,
  video: videoIcon,
  staticImage: staticImageIcon,
};

const TITLE_KEYS: Record<ShotTypeIndicatorKind, string> = {
  animatic: 'sceneBoardBadgeAnimatic',
  video: 'sceneBoardBadgeVideo',
  staticImage: 'sceneBoardBadgeStaticImage',
};

export interface ShotTypeBadgeProps {
  shot: TimelineShot;
  /** 'compact' = 16px chip (board default is 20px) for tighter hosts like the AI workspace grid. */
  size?: 'default' | 'compact';
  hasVideo?: boolean;
}

export function ShotTypeBadge({ shot, size = 'default', hasVideo }: ShotTypeBadgeProps) {
  const { t } = useTranslation('common');
  const kind = shotTypeIndicator(shot, { hasVideo });
  if (kind === null) return null;
  return (
    <span
      className={size === 'compact' ? `${styles.badge} ${styles.badgeCompact}` : styles.badge}
      title={t(TITLE_KEYS[kind])}
      data-testid={`shot-type-badge-${kind}`}
    >
      <img className={styles.icon} src={ICONS[kind]} alt="" />
    </span>
  );
}
